import React, { useEffect, useRef, useState } from "react";

import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";

//Unform e componentes
import { Form } from "@unform/web";
import UInput from "@/frontend/componentes/unform/UInput";

//Context
import { useAuth } from "@/frontend/context/auth";

//Common
import { DESCRICAO_REGRA_SENHA, ID_AUTORIZACAO } from "@/common/constantes";
import { fn_getDataHoraAtual } from "@/common/utils/date";

//Util
import { ICONES_ACESSO, ICONES_BOTAO } from "@/frontend/utils/icones";
import { useEnterAsTab } from "@/frontend/utils/useEnterAsTab.jsx";
import { fn_validarCamposObrigatorios } from "@/frontend/utils/form";
import {
  MENSAGENS_AVISO,
  MENSAGENS_ERRO,
  showMensagemAviso,
  showMensagemErro,
} from "@/frontend/utils/mensagens";

//API
import {
  api_alterarSenha,
  api_redefinirSenha,
  api_salvarLogin,
} from "@/frontend/services/api/api_iud";

//Telas
import TAutorizacao_solicitacao from "../autorizacao/autorizacao_solicitacao.jsx";
import AutorizacaoRequerida from "../autorizacao/autorizacao_requerida.jsx";
import { fn_validarSenha } from "@/frontend/utils/util.jsx";

/**
 *  Apresenta a tela de cadastro de colaborador
 */
function TUsuario_alterar_senha({
  modalOpen,
  toggleModal,
  usuarioEdit,
  redefinir,
  valorParamPrimAcesso,
}) {
  const { setDataUltimoLogin } = useAuth();

  //### USE REF ###
  const formRef = useRef(null);
  const domRef = useRef(null); // ref do wrapper DOM
  useEnterAsTab(formRef, domRef);

  //### USE STATE ###
  const [modalOpenAutorizacao, setModalOpenAutorizacao] = useState(null);

  //### USE EFFECT ###
  //Inicialização da tela
  useEffect(() => {
    //Coloca um tempo de espera para conseguir carregar a tela devido a tela ter pouca carga
    const timer = setTimeout(() => {
      //Foco no primeiro campo
      if (redefinir) {
        formRef?.current?.getFieldRef("senha")?.current?.focus();
      } else {
        formRef?.current?.getFieldRef("senha_atual")?.current?.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  //### FUNCOES ###
  //Redefine a senha após autorizar
  async function confirmarAutorizacao(dadosAutorizacao) {
    const body = formRef.current.getData();

    //Redefine a senha
    const ret = await api_redefinirSenha(
      usuarioEdit.id,
      body.senha,
      body.motivo,
      dadosAutorizacao.id_autorizacao,
      dadosAutorizacao.id_usuario,
      dadosAutorizacao.token_autorizacao,
    );

    if (ret) {
      toggleModal();
    }
  }

  //Valida os dados do formulário ao submter
  function validarFormulario(body) {
    let errors = {};

    //Valida os campos obrigatórios
    let isValido = fn_validarCamposObrigatorios(formRef, body);

    if (isValido) {
      //Valida para a nova senha não ser igual a senha atual
      if (body.senha_atual == body.senha) {
        showMensagemErro(MENSAGENS_ERRO.SENHA_NOVA_DIF_SENHA_ATUAL);
        errors["senha"] = "inválida";
        isValido = false;
      }
      //Valida a quantidade mínima de caracteres da senha
      else if (!fn_validarSenha(body.senha)) {
        errors["senha"] = "inválida";
        isValido = false;
      }
      //Valida se a confirmação da senha confere com a senha
      else if (body.senha != body.confirma_senha) {
        showMensagemErro(MENSAGENS_ERRO.INCORRETA, "Confirma nova senha");
        errors["confirma_senha"] = "inválida";
        isValido = false;
      }
      formRef.current.setErrors(errors);
    }

    return isValido;
  }

  //### EVENTOS ###
  //SUBMIT - BOTAO SALVAR
  async function handleSubmit(body) {
    if (validarFormulario(body)) {
      if (redefinir) {
        setModalOpenAutorizacao(true);
      } else {
        const ret = await api_alterarSenha(
          usuarioEdit.id,
          body.senha_atual,
          body.senha,
          valorParamPrimAcesso ? true : false,
        );

        if (ret) {
          if (valorParamPrimAcesso == "O" && !usuarioEdit.data_ultimo_login) {
            //Salva a data do login
            await api_salvarLogin(usuarioEdit.id);

            //Atualiza a data do último login na sessão para não solicitar alteração de senha quando der refresh na tela
            setDataUltimoLogin(fn_getDataHoraAtual());
          }
          toggleModal();
        }
      }
    }
  }

  //OnClick - Botão Fechar
  function fechar() {
    if (valorParamPrimAcesso == "O") {
      showMensagemAviso(
        MENSAGENS_AVISO.SENHA_DEVE_SER_ALTERADA_PRIMEIRO_ACESSO,
      );
    } else {
      toggleModal();
    }
  }

  return (
    <Modal isOpen={modalOpen} backdrop="static" keyboard={false} size="lg">
      <div ref={domRef} className="m-1">
        <Form ref={formRef} onSubmit={handleSubmit}>
          <ModalHeader toggle={fechar} className="titulo-tela-modal">
            {redefinir ? "Redefinir Senha" : "Alterar Senha"}
          </ModalHeader>

          <ModalBody>
            <Row className="mb-2">
              <Col>
                <div className="d-flex align-items-center">
                  <Label className="fw-bold me-2 mb-0">Usuario:</Label>
                  {usuarioEdit.nome}
                </div>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <div className="d-flex align-items-center">
                  <Label className="fw-bold me-2 mb-0">Identificação:</Label>
                  {usuarioEdit.identificacao}
                </div>
              </Col>
            </Row>

            <Row>
              {!redefinir && (
                <Col>
                  <UInput
                    type="password"
                    name="senha_atual"
                    label="Senha atual*"
                    caseSensitive={true}
                    maxLength={20}
                    icone={ICONES_ACESSO.SENHA}
                  />
                </Col>
              )}
              <Col>
                <UInput
                  type="password"
                  name="senha"
                  label="Nova senha*"
                  caseSensitive={true}
                  maxLength={20}
                  icone={ICONES_ACESSO.SENHA}
                  footer={DESCRICAO_REGRA_SENHA}
                />
              </Col>
              <Col>
                <UInput
                  id={redefinir ? "ConfSenha" : "lastField"}
                  type="password"
                  name="confirma_senha"
                  label="Confirma nova senha*"
                  caseSensitive={true}
                  maxLength={20}
                  icone={ICONES_ACESSO.SENHA}
                />
              </Col>
            </Row>

            <div>
              <small></small>
            </div>

            {redefinir && (
              <Row>
                <Col>
                  <UInput
                    id="lastField"
                    name="motivo"
                    label="Motivo*"
                    maxLength={80}
                  />
                </Col>
              </Row>
            )}

            {redefinir && <AutorizacaoRequerida />}
          </ModalBody>

          <ModalFooter>
            <Button
              type="submit"
              name="btnSalvar"
              data-action="salvar"
              color={redefinir ? "danger" : "primary"}
            >
              {ICONES_BOTAO.SALVAR} Salvar
            </Button>

            <Button name="btnFechar" color="secondary" onClick={fechar}>
              {ICONES_BOTAO.FECHAR} Fechar
            </Button>
          </ModalFooter>
        </Form>
      </div>

      {/* MODAL AUTORIZACAO QUANDO FOR REDEFINIR SENHA */}
      {modalOpenAutorizacao && (
        <div>
          <TAutorizacao_solicitacao
            modalOpen={modalOpenAutorizacao}
            toggleModal={() => setModalOpenAutorizacao(!modalOpenAutorizacao)}
            idTenant={usuarioEdit ? usuarioEdit.id_tenant : null}
            idAutorizacao={ID_AUTORIZACAO.SYS_ADM_REDEFINIR_SENHA}
            showMotivo={false}
            onSuccess={(dadosAutorizacao) =>
              confirmarAutorizacao(dadosAutorizacao)
            }
          />
        </div>
      )}
    </Modal>
  );
}

export default TUsuario_alterar_senha;
